import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Tela } from "@/components/casca/CabecalhoPagina";
import { Vazio } from "@/components/casca/Vazio";
import { GastoDoMes } from "@/components/relatorio/GastoDoMes";
import { CabecalhoResultados, Kpi } from "@/components/relatorio/Resultados";
import { Icone, type NomeIcone } from "@/components/Icone";
import { duracaoHumana, moeda } from "@/lib/painel/formato";
import { relatorioQuery } from "../consultas";

/**
 * Quanto custa cada paciente que senta na cadeira — e o tempo que o dono não gastou.
 *
 * Abre pelo tempo, não pelo custo: o gancho da venda é "você para de responder
 * WhatsApp entre um paciente e outro"; o custo por comparecimento é a
 * justificativa, e vem logo abaixo, com o cartão verde.
 */
export function Relatorio() {
  const {
    data: { meses, esperandoEquipe },
  } = useSuspenseQuery(relatorioQuery());
  // Abre no último mês com movimento: no dia 1º o mês corrente está vazio, e a
  // tela inteira de "—" escondia o mês que acabou de fechar (visto em 07/10/2026).
  const ultimoComMovimento = [...meses].reverse().find((m) => m.conversas > 0) ?? meses[meses.length - 1];
  const [escolhido, setEscolhido] = useState(ultimoComMovimento?.mes ?? "");
  const atual = meses.find((m) => m.mes === escolhido) ?? ultimoComMovimento;
  const pct = (n: number, de: number) => (de ? Math.round((n / de) * 100) : 0);
  const maiuscula = (r: string) => r.charAt(0).toUpperCase() + r.slice(1);
  const mes = atual ? maiuscula(atual.rotulo) : "";

  return (
    <Tela>
      <CabecalhoResultados
        acao={
          meses.length > 1 ? (
            <select
              value={atual?.mes}
              onChange={(e) => setEscolhido(e.target.value)}
              aria-label="Mês"
              className="h-9 rounded-full border-0 bg-[var(--color-cartao)] px-3.5 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao outline-none"
            >
              {[...meses].reverse().map((m) => (
                <option key={m.mes} value={m.mes}>
                  {maiuscula(m.rotulo)}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />

      {!atual || meses.every((m) => m.conversas === 0) ? (
        <Vazio icone="relatorio" titulo="Sem dados no período">
          Com conversas e agendamentos no mês, este relatório mostra o tempo poupado, o custo por conversa e o custo por comparecimento.
        </Vazio>
      ) : (
        <div className="flex flex-col gap-5">
          {atual.conversas > 0 && (
            <section className="flex flex-wrap items-center gap-7 rounded-[14px] bg-[var(--color-cartao)] p-5 shadow-cartao md:p-6">
              <Anel valor={pct(atual.soAgente, atual.conversas)} />
              <div className="flex min-w-[260px] flex-1 flex-col gap-2.5">
                <p className="rotulo">O que você não precisou fazer · {mes}</p>
                <h2 className="text-xl leading-snug font-semibold tracking-tight md:text-2xl">
                  Em{" "}
                  <b className="font-bold text-[var(--color-marca-forte)]">
                    {atual.soAgente} de {atual.conversas}
                  </b>{" "}
                  {atual.conversas === 1 ? "conversa" : "conversas"} você não precisou entrar.
                </h2>
                <ul className="flex flex-col gap-2 text-[var(--color-suave)]">
                  {atual.foraDoExpediente > 0 && (
                    <Fato icone="lua">
                      <b className="text-[var(--color-tinta)]">{atual.foraDoExpediente}</b> {atual.foraDoExpediente === 1 ? "chegou" : "chegaram"} com
                      a clínica fechada, e {atual.foraDoExpediente === 1 ? "foi atendida" : "foram atendidas"} na hora.
                    </Fato>
                  )}
                  {atual.respostaSegundos !== null && (
                    <Fato icone="conversas">
                      Primeira resposta em <b className="text-[var(--color-tinta)]">{duracaoHumana(atual.respostaSegundos)}</b>, na mediana.
                    </Fato>
                  )}
                  {atual.avisosEquipe > 0 && (
                    <Fato icone="equipe">
                      Em <b className="text-[var(--color-tinta)]">{atual.avisosEquipe}</b> o assistente passou para a equipe
                      {atual.retornoEquipeSegundos !== null && (
                        <>
                          , que deu o retorno em <b className="text-[var(--color-tinta)]">{duracaoHumana(atual.retornoEquipeSegundos)}</b>
                        </>
                      )}
                      .
                    </Fato>
                  )}
                </ul>
              </div>
            </section>
          )}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(170px,100%),1fr))] gap-3">
            <Kpi
              destaque
              rotulo="Por comparecimento"
              valor={moeda(atual.custoPorComparecimento)}
              nota={`${atual.compareceram} ${atual.compareceram === 1 ? "apareceu" : "apareceram"}`}
            />
            <Kpi rotulo="Por agendamento" valor={moeda(atual.custoPorAgendamento)} nota={`${atual.agendamentos} ${atual.agendamentos === 1 ? "marcado" : "marcados"}`} />
            <Kpi rotulo="Por conversa" valor={moeda(atual.custoPorConversa)} nota={`${atual.conversas} ${atual.conversas === 1 ? "conversa" : "conversas"}`} />
            <Kpi rotulo="Fora do expediente" valor={`${pct(atual.foraDoExpediente, atual.conversas)}%`} nota={`${atual.foraDoExpediente} das conversas`} />
            <Kpi rotulo="Tempo de resposta" valor={duracaoHumana(atual.respostaSegundos)} nota="mediana no mês" />
          </div>

          {esperandoEquipe > 0 && (
            <Alerta tom="alerta" icone="equipe" acao={<Link to="/conversas">Ver em Conversas</Link>}>
              <b>{esperandoEquipe === 1 ? "1 paciente espera" : `${esperandoEquipe} pacientes esperam`}</b> um retorno que o assistente prometeu em
              nome da equipe.
            </Alerta>
          )}

          {atual.semMarcar > 0 && (
            <Alerta tom="atencao" icone="agenda" acao={<Link to="/agenda">Abrir a Agenda</Link>}>
              <b>
                {atual.semMarcar === 1 ? `1 agendamento de ${atual.rotulo} já passou` : `${atual.semMarcar} agendamentos de ${atual.rotulo} já passaram`} sem presença
                marcada.
              </b>{" "}
              Enquanto isso, o custo por comparecimento fica alto demais: ele conta o gasto inteiro contra menos gente do que realmente foi.
            </Alerta>
          )}

          <div className="overflow-x-auto rounded-[14px] bg-[var(--color-cartao)] shadow-cartao">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="text-xs font-bold tracking-[0.05em] text-[var(--color-fraco)] uppercase">
                <tr className="border-b border-[var(--color-borda)]">
                  {["Mês", "Gasto em mídia", "Conversas", "Sem você", "1ª resposta", "Retorno da equipe", "Agendou", "Compareceu", "Por agendamento", "Por comparecimento"].map(
                    (t) => (
                      <th key={t} className="px-3.5 py-3 whitespace-nowrap">
                        {t}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {meses.map((m) => (
                  <tr key={m.mes} className="border-b border-[var(--color-borda)] last:border-0 hover:bg-[var(--color-painel)]">
                    <td className={"px-3.5 py-3 font-semibold whitespace-nowrap " + (m.mes === atual.mes ? "text-[var(--color-marca-forte)]" : "")}>{maiuscula(m.rotulo)}</td>
                    <td className="px-3.5 py-2">
                      <GastoDoMes mes={m.mes} gasto={m.gasto} />
                    </td>
                    <td className="px-3.5 py-3">{m.conversas}</td>
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      {m.soAgente}
                      {m.foraDoExpediente > 0 && <span className="text-[var(--color-fraco)]"> · {m.foraDoExpediente} fora</span>}
                    </td>
                    <td className="px-3.5 py-3 whitespace-nowrap text-[var(--color-suave)]">{duracaoHumana(m.respostaSegundos)}</td>
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      {m.avisosEquipe === 0 ? (
                        <span className="text-[var(--color-fraco)]">—</span>
                      ) : (
                        <>
                          <span className="text-[var(--color-suave)]">{duracaoHumana(m.retornoEquipeSegundos)}</span>
                          <span className="text-xs text-[var(--color-fraco)]">
                            {" "}
                            · {m.avisosEquipe} {m.avisosEquipe === 1 ? "aviso" : "avisos"}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="px-3.5 py-3">{m.agendamentos}</td>
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <span className="font-semibold">{m.compareceram}</span>
                      {m.faltaram > 0 && <span className="text-xs text-[var(--color-atencao)]"> · {m.faltaram} faltou</span>}
                      {m.semMarcar > 0 && <span className="text-xs text-[var(--color-fraco)]"> · {m.semMarcar} pendente</span>}
                    </td>
                    <td className="px-3.5 py-3 whitespace-nowrap">{moeda(m.custoPorAgendamento)}</td>
                    <td className="px-3.5 py-3 font-bold whitespace-nowrap">{moeda(m.custoPorComparecimento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[13px] leading-relaxed text-[var(--color-fraco)]">
            Conversas conta gente nova — a primeira vez que cada pessoa escreveu. Agendou conta horários marcados para o mês. Retorno da equipe é o
            tempo, na mediana, entre o assistente passar a conversa para a equipe e alguém marcar como resolvido. O gasto de mídia é digitado por
            enquanto (toque no valor para mudar); quando houver integração com o gerenciador de anúncios, ele entra sozinho.
          </p>
        </div>
      )}
    </Tela>
  );
}

/** O anel da porcentagem de conversas que o assistente resolveu sozinho. */
function Anel({ valor }: { valor: number }) {
  const volta = 2 * Math.PI * 62;
  return (
    <div className="relative hidden h-[148px] w-[148px] shrink-0 sm:block" aria-hidden>
      <svg viewBox="0 0 148 148" className="h-full w-full -rotate-90">
        <circle cx="74" cy="74" r="62" fill="none" stroke="var(--color-painel)" strokeWidth="14" />
        <circle
          cx="74"
          cy="74"
          r="62"
          fill="none"
          stroke="var(--color-marca)"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${(valor / 100) * volta} ${volta}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <b className="num text-[30px] tracking-tight">{valor}%</b>
        <span className="text-[11.5px] font-semibold text-[var(--color-fraco)]">sem você entrar</span>
      </div>
    </div>
  );
}

function Fato({ icone, children }: { icone: NomeIcone; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Icone nome={icone} className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-marca-forte)]" />
      <span>{children}</span>
    </li>
  );
}

function Alerta({ tom, icone, acao, children }: { tom: "alerta" | "atencao"; icone: NomeIcone; acao: ReactNode; children: ReactNode }) {
  return (
    <div
      className={
        "flex flex-wrap items-center gap-3 rounded-[14px] px-4 py-3 text-sm " +
        (tom === "alerta" ? "bg-[var(--color-alerta-fundo)] text-[var(--color-alerta)]" : "bg-[var(--color-atencao-fundo)] text-[var(--color-atencao)]")
      }
    >
      <Icone nome={icone} className="h-5 w-5 shrink-0" />
      <p className="min-w-[220px] flex-1 leading-relaxed">{children}</p>
      <span className="[&>a]:flex [&>a]:h-8 [&>a]:items-center [&>a]:rounded-full [&>a]:bg-[var(--color-cartao)] [&>a]:px-3.5 [&>a]:text-[13px] [&>a]:font-semibold [&>a]:text-[var(--color-tinta)] [&>a]:shadow-botao [&>a:hover]:bg-[var(--color-painel)]">
        {acao}
      </span>
    </div>
  );
}
